/**
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2026
 */

import { useEffect, useState } from 'react'
import {
  getLayoutState,
  setPdfLoadFailed,
  useDispatch,
  useSelector,
} from '@/store'

type PdfAvailability = 'checking' | 'available' | 'failed'

/**
 * A missing PDF path can still resolve to a 200 response (e.g. the
 * dev server's SPA fallback serving index.html), so <object>/<embed>
 * never fire their error event. Checking the actual content-type
 * catches that case too, not just network failures.
 */
export function usePdfAvailability(pdfDoc: string): PdfAvailability {
  const dispatch = useDispatch()
  const { failedPdfUrls } = useSelector(getLayoutState)
  const [status, setStatus] = useState<PdfAvailability>(
    failedPdfUrls[pdfDoc] ? 'failed' : 'checking'
  )

  useEffect(() => {
    if (failedPdfUrls[pdfDoc]) {
      setStatus('failed')
      return
    }

    let cancelled = false

    fetch(pdfDoc, { method: 'HEAD' })
      .then((response) => {
        if (cancelled) return
        const contentType = response.headers.get('content-type') ?? ''
        if (response.ok && contentType.includes('application/pdf')) {
          setStatus('available')
        } else {
          setStatus('failed')
          dispatch(setPdfLoadFailed(pdfDoc))
        }
      })
      .catch(() => {
        if (cancelled) return
        setStatus('failed')
        dispatch(setPdfLoadFailed(pdfDoc))
      })

    return () => {
      cancelled = true
    }
  }, [pdfDoc])

  return status
}
